// AdminDashboard.jsx
// שים ב: src/components/AdminDashboard.jsx
// route: /yk6524tshc — מוגן בסיסמה

import React, { useState, useEffect } from 'react';
import {
    Plane, Search, Plus, LogOut, Users, FolderOpen,
    Star, ChevronLeft, Menu, X, Loader, Edit3,
    Lock, Unlock, Shield, AlertTriangle, Archive, Clock, Trash2, Copy, RefreshCw, CheckCircle
} from 'lucide-react';
import {
    collection, onSnapshot, query, orderBy, doc, deleteDoc, updateDoc, setDoc, getDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import TripForm from '../TripForm';

// ─── AdminGate ────────────────────────────────────────────
function AdminGate({ children }) {
    const [authed, setAuthed] = useState(false);
    const [pass, setPass] = useState('');
    const [error, setError] = useState(false);
    const [shake, setShake] = useState(false);

    if (authed) return children;

    const handleSubmit = (e) => {
        e.preventDefault();
        if (pass === process.env.REACT_APP_ADMIN_PASS) {

            setAuthed(true);
        } else {
            setError(true);
            setShake(true);
            setPass('');
            setTimeout(() => setShake(false), 600);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#060F2A] via-[#0D1F4A] to-[#0D1B3E] flex items-center justify-center p-4">
            <style>{`
        @keyframes shake {
          0%,100% { transform: translateX(0); }
          20%,60%  { transform: translateX(-8px); }
          40%,80%  { transform: translateX(8px); }
        }
        .mgd-shake { animation: shake 0.5s ease; }
      `}</style>
            <div className={"w-full max-w-sm " + (shake ? "mgd-shake" : "")}>
                <div className="text-center mb-8">
                    <a href="/" title="חזרה לאתר">
                        <img src="/megedLogoTransp.png" alt="מגד שמיים" className="w-16 h-16 mx-auto rounded-2xl object-contain mb-4 hover:opacity-80 transition-opacity cursor-pointer" />
                    </a>
                    <h1 className="text-white font-black text-2xl">מגד שמים</h1>
                    <p className="text-blue-400 text-sm mt-1">כניסה למערכת ניהול</p>
                </div>
                <form onSubmit={handleSubmit}
                    className="bg-[#0D1F4A]/80 border border-[#1B3F8B]/50 rounded-2xl p-6 backdrop-blur-sm"
                    dir="rtl">
                    <div className="flex flex-col gap-1 mb-4">
                        <label className="text-blue-300 text-sm font-medium">סיסמה</label>
                        <input
                            type="password"
                            value={pass}
                            onChange={e => { setPass(e.target.value); setError(false); }}
                            autoFocus
                            className={"bg-[#060F2A]/60 border text-white rounded-xl px-4 py-3 focus:outline-none transition-all " +
                                (error ? "border-red-500/70" : "border-[#1B3F8B]/50 focus:border-[#D4A017]/70")}
                            dir="ltr"
                        />
                        {error && <p className="text-red-400 text-xs mt-1">סיסמה שגויה</p>}
                    </div>
                    <button type="submit"
                        className="w-full py-3 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#F5C842] text-[#060F2A] font-bold hover:from-[#F5C842] hover:to-[#D4A017] transition-all">
                        כניסה
                    </button>
                </form>
            </div>
        </div>
    );
}

// ─── דגל מדינה ────────────────────────────────────────────
const COUNTRY_FLAGS = {
    "ישראל": "🇮🇱", "איטליה": "🇮🇹", "ספרד": "🇪🇸", "יוון": "🇬🇷", "צרפת": "🇫🇷", "פורטוגל": "🇵🇹",
    "גרמניה": "🇩🇪", "אוסטריה": "🇦🇹", "שוויץ": "🇨🇭", "הולנד": "🇳🇱", "בריטניה": "🇬🇧",
    "טורקיה": "🇹🇷", "גיאורגיה": "🇬🇪", "אמירויות": "🇦🇪", "ירדן": "🇯🇴", "מצרים": "🇪🇬",
    "תאילנד": "🇹🇭", "יפן": "🇯🇵", "קוריאה": "🇰🇷", "הודו": "🇮🇳", "ארה\"ב": "🇺🇸", "קנדה": "🇨🇦",
    "אוסטרליה": "🇦🇺", "מרוקו": "🇲🇦", "פולין": "🇵🇱", "צ'כיה": "🇨🇿", "הונגריה": "🇭🇺",
};
const getFlag = (dest) => dest ? (COUNTRY_FLAGS[dest] || "🌍") : "";
const FadeIn = ({ children, delay = 0 }) => (
    <div style={{ animation: `fadeInUp 0.5s ease ${delay}s both` }}>{children}</div>
);

// ─── Delete Modal ─────────────────────────────────────────
const DeleteConfirmModal = ({ trip, onConfirm, onCancel }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
        <div className="relative bg-[#0D1F4A] border border-red-500/40 rounded-2xl p-6 w-full max-w-md shadow-2xl shadow-red-500/20">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/20 border border-red-500/40 flex items-center justify-center">
                    <AlertTriangle size={24} className="text-red-400" />
                </div>
                <div>
                    <h3 className="text-white font-bold text-lg">מחיקת תיק טיול</h3>
                    <p className="text-blue-400 text-sm">פעולה זו אינה הפיכה</p>
                </div>
            </div>
            <p className="text-blue-200 mb-2">האם אתה בטוח שברצונך למחוק את תיק הטיול של:</p>
            <p className="text-white font-bold text-lg mb-1">{trip.travelerName}</p>
            <p className="text-blue-400 text-sm mb-6">ת.ז: {trip.tripId} | {trip.destination || "יעד לא הוגדר"}</p>
            <div className="flex gap-3">
                <button onClick={onCancel}
                    className="flex-1 py-3 rounded-xl border border-[#1B3F8B] text-blue-300 hover:border-[#D4A017] hover:text-[#D4A017] transition-all font-medium">
                    ביטול
                </button>
                <button onClick={onConfirm}
                    className="flex-1 py-3 rounded-xl bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30 transition-all font-bold">
                    מחק לצמיתות
                </button>
            </div>
        </div>
    </div>
);

// ─── Security Panel ───────────────────────────────────────
const SecurityPanel = ({ trip, onClose }) => {
    const [locking, setLocking] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    const [done, setDone] = useState("");
    const [copied, setCopied] = useState(false);
    const isLocked = trip.lockedUntil && new Date(trip.lockedUntil) > new Date();

    const handleLock = async () => {
        setLocking(true);
        try {
            await updateDoc(doc(db, "trips", trip.docId || trip.tripId), {
                lockedUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), loginAttempts: 5,
            });
            setDone("locked");
        } finally { setLocking(false); }
    };
    const handleUnlock = async () => {
        setUnlocking(true);
        try {
            await updateDoc(doc(db, "trips", trip.docId || trip.tripId), { lockedUntil: null, loginAttempts: 0 });
            setDone("unlocked");
        } finally { setUnlocking(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" dir="rtl">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-[#0D1F4A] border border-[#1B3F8B]/60 rounded-2xl p-6 w-full max-w-md shadow-2xl">
                <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#1B3F8B]/40 border border-[#1B3F8B]/60 flex items-center justify-center">
                            <Shield size={20} className="text-[#D4A017]" />
                        </div>
                        <div>
                            <h3 className="text-white font-bold">אבטחת חשבון</h3>
                            <p className="text-blue-400 text-xs">{trip.travelerName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-blue-500 hover:text-white transition-colors"><X size={20} /></button>
                </div>
                {trip.authCode && (
                    <div className="flex items-center gap-3 bg-[#060F2A]/60 rounded-xl px-4 py-3 mb-5 border border-[#1B3F8B]/40">
                        <Lock size={16} className="text-[#D4A017] shrink-0" />
                        <span className="text-blue-400 text-sm">קוד אימות:</span>
                        <span className="text-2xl font-black text-[#D4A017] tracking-[0.25em] flex-1">{trip.authCode}</span>
                        <button onClick={() => { navigator.clipboard.writeText(trip.authCode); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                            className="p-1.5 rounded-lg bg-[#1B3F8B]/40 text-blue-400 hover:text-[#D4A017] transition-colors">
                            {copied ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
                        </button>
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="bg-[#060F2A]/60 rounded-xl p-3 border border-[#1B3F8B]/40">
                        <p className="text-blue-400 text-xs mb-1">ניסיונות כושלים</p>
                        <p className={"text-2xl font-black " + ((trip.loginAttempts || 0) >= 3 ? "text-red-400" : "text-white")}>
                            {trip.loginAttempts || 0} / 5
                        </p>
                    </div>
                    <div className={"rounded-xl p-3 border " + (isLocked ? "bg-red-500/10 border-red-500/40" : "bg-green-500/10 border-green-500/30")}>
                        <p className="text-blue-400 text-xs mb-1">סטטוס</p>
                        <p className={"text-lg font-bold " + (isLocked ? "text-red-400" : "text-green-400")}>
                            {isLocked ? "🔒 נעול" : "🔓 פתוח"}
                        </p>
                    </div>
                </div>
                {trip.lastLogin && <p className="text-blue-500 text-xs mb-5">כניסה אחרונה: {new Date(trip.lastLogin).toLocaleString("he-IL")}</p>}
                {done === "locked" && <p className="text-red-400 text-sm mb-4 flex items-center gap-2"><Lock size={14} /> המשתמש ננעל</p>}
                {done === "unlocked" && <p className="text-green-400 text-sm mb-4 flex items-center gap-2"><Unlock size={14} /> הנעילה שוחררה</p>}
                <div className="flex gap-3">
                    {!isLocked
                        ? <button onClick={handleLock} disabled={locking}
                            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-red-500/15 border border-red-500/40 text-red-400 hover:bg-red-500/25 disabled:opacity-50 font-semibold text-sm transition-all">
                            <Lock size={16} /> {locking ? "נועל..." : "נעל משתמש"}
                        </button>
                        : <button onClick={handleUnlock} disabled={unlocking}
                            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-green-500/15 border border-green-500/40 text-green-400 hover:bg-green-500/25 disabled:opacity-50 font-semibold text-sm transition-all">
                            <Unlock size={16} /> {unlocking ? "משחרר..." : "שחרר נעילה"}
                        </button>
                    }
                    <button onClick={onClose}
                        className="flex-1 py-3 rounded-xl border border-[#1B3F8B]/50 text-blue-300 hover:border-[#D4A017]/40 hover:text-[#D4A017] transition-all font-medium text-sm">
                        סגור
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── StatCard ─────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, color }) => (
    <div className={"relative overflow-hidden rounded-2xl p-5 border " + color + " backdrop-blur-sm"}>
        <div className="flex items-center justify-between">
            <div>
                <p className="text-blue-300 text-sm mb-1">{label}</p>
                <p className="text-3xl font-bold text-white">{value}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                <Icon size={24} className="text-[#D4A017]" />
            </div>
        </div>
        <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-white/5" />
    </div>
);

// ─── TripCard ─────────────────────────────────────────────
const TripCard = ({ trip, onOpen, onEdit, onDelete, onSecurity }) => {
    const [confirmDelete, setConfirmDelete] = useState(false);
    const isLocked = trip.lockedUntil && new Date(trip.lockedUntil) > new Date();
    return (
        <>
            <div className="bg-[#0D1F4A]/60 border border-[#1B3F8B]/50 rounded-2xl p-5 hover:border-[#D4A017]/40 transition-all duration-300 hover:shadow-xl group backdrop-blur-sm">
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                        <h3 className="text-white font-bold text-lg group-hover:text-[#D4A017] transition-colors">{trip.travelerName}</h3>
                        <p className="text-blue-400 text-sm">ת.ז: {trip.tripId}</p>
                        {trip.travelerPhone && <p className="text-blue-500 text-xs mt-0.5">📞 {trip.travelerPhone}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        {trip.destination && (
                            <span className="bg-[#D4A017]/20 text-[#D4A017] text-xs px-3 py-1 rounded-full border border-[#D4A017]/30">
                                {getFlag(trip.destination)} {trip.destination}
                            </span>
                        )}
                        {isLocked && (
                            <span className="bg-red-500/20 text-red-400 text-xs px-2 py-0.5 rounded-full border border-red-500/30 flex items-center gap-1">
                                <Lock size={10} /> נעול
                            </span>
                        )}
                    </div>
                </div>
                {(trip.startDate || trip.endDate) && (
                    <p className="text-blue-400 text-sm mb-3 flex items-center gap-2" dir="ltr">
                        <Star size={12} className="text-[#D4A017]" />
                        {trip.startDate?.split("-").reverse().join("/")} — {trip.endDate?.split("-").reverse().join("/")}
                    </p>
                )}
                <div className="flex items-center gap-4 mb-4 text-blue-400 text-sm">
                    <span>📅 {trip.itinerary?.length || 0} ימים</span>
                    <span>📄 {trip.documents?.length || 0} מסמכים</span>
                    <span>👥 {trip.contacts?.length || 0} אנשי קשר</span>
                </div>
                <div className="flex items-center gap-2 pt-3 border-t border-[#1B3F8B]/50">
                    <button onClick={() => onOpen(trip)}
                        className="flex-1 py-2 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#F5C842] text-[#060F2A] font-bold text-sm hover:from-[#F5C842] hover:to-[#D4A017] transition-all flex items-center justify-center gap-2">
                        פתח תיק <ChevronLeft size={16} />
                    </button>
                    <button onClick={() => onEdit(trip)}
                        className="p-2 rounded-xl border border-[#1B3F8B]/50 text-blue-400 hover:text-[#D4A017] hover:border-[#D4A017]/40 transition-all" title="ערוך">
                        <Edit3 size={16} />
                    </button>
                    <button onClick={() => onSecurity(trip)}
                        className={"p-2 rounded-xl border transition-all " + (isLocked ? "border-red-500/40 text-red-400" : "border-[#1B3F8B]/50 text-blue-400 hover:text-[#D4A017] hover:border-[#D4A017]/40")}
                        title="אבטחה">
                        <Shield size={16} />
                    </button>
                    <button onClick={() => setConfirmDelete(true)}
                        className="p-2 rounded-xl border border-[#1B3F8B]/50 text-blue-500 hover:text-red-400 hover:border-red-500/40 transition-all" title="מחק">
                        <X size={16} />
                    </button>
                </div>
            </div>
            {confirmDelete && (
                <DeleteConfirmModal trip={trip}
                    onConfirm={() => { setConfirmDelete(false); onDelete(trip); }}
                    onCancel={() => setConfirmDelete(false)} />
            )}
        </>
    );
};

// ─── Sidebar ──────────────────────────────────────────────
const Sidebar = ({ currentView, onNavigate, sidebarOpen, setSidebarOpen }) => {
    const navItems = [
        { id: "dashboard", label: "לוח בקרה", icon: Star },
        { id: "trips", label: "תיקי טיול", icon: FolderOpen },
        { id: "past-trips", label: "טיולי עבר", icon: Clock },
        { id: "archive", label: "ארכיון", icon: Archive },
        { id: "new-trip", label: "תיק חדש", icon: Plus },
    ];
    return (
        <>
            {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />}
            <aside className={"fixed top-0 right-0 h-full w-72 bg-gradient-to-b from-[#060F2A] to-[#0D1F4A] border-l border-[#1B3F8B]/50 z-30 transform transition-transform duration-300 " + (sidebarOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0")} dir="rtl">
                <div className="px-6 pt-8 pb-6 border-b border-[#1B3F8B]/40">
                    <div className="flex items-center gap-3">
                        <img src="/megedLogoTransp.png" alt="מגד שמיים" className="w-10 h-10 rounded-xl object-contain border border-[#1B3F8B]/40" />
                        <div>
                            <h1 className="text-white font-black text-xl">מגד שמים</h1>
                            <p className="text-[#D4A017]/70 text-xs">ניהול תיקי טיול</p>
                        </div>
                    </div>
                </div>
                <nav className="px-4 py-6 space-y-1">
                    {navItems.map(({ id, label, icon: Icon }) => (
                        <button key={id} onClick={() => { onNavigate(id); setSidebarOpen(false); }}
                            className={"w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all " +
                                (currentView === id ? "bg-[#1B3F8B]/50 text-white border border-[#D4A017]/30" : "text-blue-300 hover:bg-[#1B3F8B]/20 hover:text-white")}>
                            <Icon size={18} className={currentView === id ? "text-[#D4A017]" : ""} /> {label}
                        </button>
                    ))}
                </nav>
                <div className="absolute bottom-24 left-0 right-0 flex items-center justify-center pointer-events-none px-6">
                    <img src="/backgroundLogo.png" alt="" className="w-44 opacity-30 select-none" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#1B3F8B]/40">
                    <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-[#1B3F8B]/20">
                        <img src="/megedLogoTransp.png" alt="" className="w-8 h-8 rounded-full object-contain border border-[#1B3F8B]/40" />
                        <div className="flex-1">
                            <p className="text-white text-sm font-medium">מנהל מערכת</p>
                            <p className="text-[#D4A017]/60 text-xs">מגד שמים</p>
                        </div>
                        <button onClick={() => { sessionStorage.removeItem('__mgd_ok'); window.location.reload(); }}
                            className="text-blue-500 hover:text-red-400 transition-colors">
                            <LogOut size={16} />
                        </button>
                    </div>
                </div>
            </aside>
        </>
    );
};

// ─── DashboardView ────────────────────────────────────────
const DashboardView = ({ trips, onNavigate, onOpen }) => {
    const totalDays = trips.reduce((s, t) => s + (t.itinerary?.length || 0), 0);
    return (
        <div className="space-y-8" dir="rtl">
            <FadeIn>
                <div>
                    <h2 className="text-3xl font-black text-white mb-1">שלום 👋</h2>
                    <p className="text-blue-400">ברוכים הבאים למערכת ניהול הטיולים</p>
                </div>
            </FadeIn>
            <FadeIn delay={0.1}>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard label="תיקי טיול" value={trips.length} icon={FolderOpen} color="bg-[#0D1F4A]/80 border-[#1B3F8B]/50" />
                    <StatCard label="מטיילים" value={trips.length} icon={Users} color="bg-[#0D1F4A]/80 border-[#1B3F8B]/50" />
                    <StatCard label='סה"כ ימים' value={totalDays} icon={Star} color="bg-[#0D1F4A]/80 border-[#1B3F8B]/50" />
                    <StatCard label="מסמכים" value={trips.reduce((s, t) => s + (t.documents?.length || 0), 0)} icon={FolderOpen} color="bg-[#0D1F4A]/80 border-[#1B3F8B]/50" />
                </div>
            </FadeIn>
            <FadeIn delay={0.2}>
                <div className="bg-gradient-to-r from-[#D4A017]/10 to-[#D4A017]/5 border border-[#D4A017]/20 rounded-2xl p-6">
                    <h3 className="text-white font-bold mb-4 text-lg">פעולות מהירות</h3>
                    <div className="flex flex-wrap gap-3">
                        <button onClick={() => onNavigate("new-trip")}
                            className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#F5C842] text-[#060F2A] font-bold hover:from-[#F5C842] hover:to-[#D4A017] transition-all shadow-lg shadow-[#D4A017]/25">
                            <Plus size={18} /> פתח תיק טיול חדש
                        </button>
                        <button onClick={() => onNavigate("trips")}
                            className="flex items-center gap-2 px-5 py-3 rounded-xl border border-[#1B3F8B] text-blue-300 hover:border-[#D4A017] hover:text-[#D4A017] transition-all">
                            <FolderOpen size={18} /> כל תיקי הטיול
                        </button>
                    </div>
                </div>
            </FadeIn>
            {trips.length > 0 && (
                <FadeIn delay={0.3}>
                    <div>
                        <h3 className="text-white font-bold mb-4 text-lg">תיקים אחרונים</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {trips.slice(0, 3).map(t => (
                                <div key={t.tripId}
                                    onClick={() => onOpen(t)}
                                    className="bg-[#0D1F4A]/60 border border-[#1B3F8B]/50 rounded-2xl p-4 cursor-pointer hover:border-[#D4A017]/40 hover:shadow-lg transition-all group">
                                    <p className="text-white font-bold group-hover:text-[#D4A017] transition-colors">{t.travelerName}</p>
                                    <p className="text-blue-400 text-sm">{getFlag(t.destination)} {t.destination || "יעד לא הוגדר"}</p>
                                    {t.startDate && <p className="text-[#D4A017]/70 text-xs mt-1" dir="ltr">📅 {t.startDate.split("-").reverse().join("/")} — {t.endDate?.split("-").reverse().join("/")}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                </FadeIn>
            )}
        </div>
    );
};

// ─── TripsView ────────────────────────────────────────────
const TripsView = ({ trips, onOpen, onEdit, onDelete, onSecurity, onNavigate }) => {
    const [search, setSearch] = useState("");
    const filtered = trips.filter(t =>
        t.travelerName?.includes(search) || t.tripId?.includes(search) || t.destination?.includes(search)
    );
    return (
        <div dir="rtl">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-black text-white">תיקי טיול</h2>
                    <p className="text-blue-400 text-sm">{trips.length} תיקים במערכת</p>
                </div>
                <button onClick={() => onNavigate("new-trip")}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#F5C842] text-[#060F2A] font-bold hover:from-[#F5C842] hover:to-[#D4A017] transition-all">
                    <Plus size={18} /> תיק חדש
                </button>
            </div>
            <div className="relative mb-6">
                <Search size={18} className="absolute top-1/2 right-4 -translate-y-1/2 text-blue-500" />
                <input type="text" placeholder="חפש לפי שם, ת.ז. או יעד..." value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full bg-[#0D1F4A]/60 border border-[#1B3F8B]/50 text-white placeholder-blue-500 rounded-xl pr-11 pl-4 py-3 focus:outline-none focus:border-[#D4A017]/70 transition-all" />
            </div>
            {filtered.length === 0
                ? <div className="text-center py-20 text-blue-500"><FolderOpen size={48} className="mx-auto mb-4 opacity-30" /><p>לא נמצאו תיקים</p></div>
                : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map(t => <TripCard key={t.docId || t.tripId} trip={t} onOpen={onOpen} onEdit={onEdit} onDelete={onDelete} onSecurity={onSecurity} />)}
                </div>
            }
        </div>
    );
};

// ─── Main Dashboard App ───────────────────────────────────
function DashboardApp() {
    const [currentView, setCurrentView] = useState("dashboard");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [trips, setTrips] = useState([]);
    const [archivedTrips, setArchivedTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [tripMode, setTripMode] = useState("view");
    const [securityTrip, setSecurityTrip] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    useEffect(() => {
        const q = query(collection(db, "trips"), orderBy("updatedAt", "desc"));
        const unsub = onSnapshot(q, snap => {
            setTrips(snap.docs.map(d => ({ ...d.data(), docId: d.id })));
            setLoading(false);
        }, err => { console.error(err); setLoading(false); });
        return () => unsub();
    }, []);

    useEffect(() => {
        const q = query(collection(db, "archive"), orderBy("archivedAt", "desc"));
        const unsub = onSnapshot(q, snap => {
            setArchivedTrips(snap.docs.map(d => ({ ...d.data(), docId: d.id })));
        }, () => { });
        return () => unsub();
    }, []);

    const today = new Date().toISOString().split("T")[0];
    const activeTrips = trips.filter(t => !t.endDate || t.endDate >= today);
    const pastTrips = trips.filter(t => t.endDate && t.endDate < today);

    const handleDelete = async (trip) => {
        try {
            const docId = trip.docId || trip.tripId;
            await setDoc(doc(db, "archive", docId), { ...trip, archivedAt: new Date().toISOString() });
            await deleteDoc(doc(db, "trips", docId));
        } catch (err) { alert("שגיאה: " + err.message); }
    };

    const handleArchiveDelete = async (trip) => {
        try { await deleteDoc(doc(db, "archive", trip.docId)); }
        catch (err) { alert("שגיאה: " + err.message); }
    };

    const handleOpenTrip = (trip) => { setSelectedTrip(trip); setTripMode("view"); setCurrentView("trip-detail"); };
    const handleEditTrip = (trip) => { setSelectedTrip(trip); setTripMode("edit"); setCurrentView("trip-detail"); };
    const handleSaved = () => setCurrentView("trips");

    const renderContent = () => {
        if (loading) return (
            <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center gap-4 text-blue-400">
                    <Loader size={40} className="animate-spin text-[#D4A017]" />
                    <p>טוען נתונים...</p>
                </div>
            </div>
        );
        switch (currentView) {
            case "dashboard": return <DashboardView trips={activeTrips} onNavigate={setCurrentView} onOpen={handleOpenTrip} />;
            case "trips": return <TripsView trips={activeTrips} onOpen={handleOpenTrip} onEdit={handleEditTrip} onDelete={t => setDeleteConfirm(t)} onSecurity={setSecurityTrip} onNavigate={setCurrentView} />;
            case "past-trips": return (
                <div dir="rtl">
                    <h2 className="text-2xl font-black text-white mb-6 flex items-center gap-3"><Clock size={24} className="text-[#D4A017]" /> טיולי עבר</h2>
                    {pastTrips.length === 0
                        ? <div className="text-center py-20 text-blue-500"><Clock size={48} className="mx-auto mb-4 opacity-30" /><p>אין טיולים שעברו</p></div>
                        : <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {pastTrips.map(t => <TripCard key={t.docId} trip={t} onOpen={handleOpenTrip} onEdit={handleEditTrip} onDelete={t2 => setDeleteConfirm(t2)} onSecurity={setSecurityTrip} />)}
                        </div>
                    }
                </div>
            );
            case "archive": return (
                <div dir="rtl">
                    <h2 className="text-2xl font-black text-white mb-2 flex items-center gap-3"><Archive size={24} className="text-[#D4A017]" /> ארכיון</h2>
                    <p className="text-blue-400 text-sm mb-6">טיולים שנמחקו</p>
                    {archivedTrips.length === 0
                        ? <div className="text-center py-20 text-blue-500"><Archive size={48} className="mx-auto mb-4 opacity-30" /><p>הארכיון ריק</p></div>
                        : <div className="space-y-3">
                            {archivedTrips.map(t => (
                                <div key={t.docId} className="bg-[#0D1F4A]/60 border border-[#1B3F8B]/40 rounded-2xl p-4 flex items-center gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-white font-bold truncate">{t.travelerName}</span>
                                            <span className="text-blue-500 text-xs">ת.ז: {t.tripId}</span>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-blue-400">
                                            {t.destination && <span>{getFlag(t.destination)} {t.destination}</span>}
                                            {t.archivedAt && <span className="text-blue-600 text-xs">נמחק: {new Date(t.archivedAt).toLocaleDateString("he-IL")}</span>}
                                        </div>
                                    </div>
                                    <button onClick={() => setDeleteConfirm({ ...t, _permanent: true })}
                                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/15 border border-red-500/40 text-red-400 hover:bg-red-500/25 transition-all text-sm font-semibold shrink-0">
                                        <Trash2 size={14} /> מחק לצמיתות
                                    </button>
                                </div>
                            ))}
                        </div>
                    }
                </div>
            );
            case "new-trip": return (
                <div dir="rtl">
                    <h2 className="text-2xl font-black text-white mb-6">פתיחת תיק טיול חדש</h2>
                    <TripForm onSaved={handleSaved} defaultReadOnly={false} />
                </div>
            );
            case "trip-detail": return (
                <div dir="rtl">
                    <button onClick={() => setCurrentView("trips")}
                        className="flex items-center gap-2 text-blue-400 hover:text-[#D4A017] mb-6 transition-colors">
                        <ChevronLeft size={18} className="rotate-180" /> חזרה לרשימה
                    </button>
                    <h2 className="text-2xl font-black text-white mb-6">
                        {tripMode === "view" ? "👁️ צפייה:" : "✏️ עריכת:"} {selectedTrip?.travelerName}
                    </h2>
                    <TripForm initialData={selectedTrip} onSaved={handleSaved} defaultReadOnly={tripMode === "view"}
                        onModeChange={isEdit => setTripMode(isEdit ? "edit" : "view")} />
                </div>
            );
            default: return null;
        }
    };

    return (
        <div id="dashboard-root">
            <div className="min-h-screen bg-gradient-to-br from-[#060F2A] via-[#0D1F4A] to-[#0D1B3E]" dir="rtl">
                <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Assistant:wght@400;600;700;800;900&display=swap');
        * { font-family: 'Assistant', sans-serif; }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        ::-webkit-scrollbar { width:6px; }
        ::-webkit-scrollbar-track { background:#060F2A; }
        ::-webkit-scrollbar-thumb { background:#1B3F8B; border-radius:3px; }
        ::-webkit-scrollbar-thumb:hover { background:#D4A017; }
        select option { background:#0D1F4A; }
      `}</style>

                {securityTrip && <SecurityPanel trip={securityTrip} onClose={() => setSecurityTrip(null)} />}
                {deleteConfirm && (
                    <DeleteConfirmModal trip={deleteConfirm}
                        onConfirm={async () => {
                            if (deleteConfirm._permanent) await handleArchiveDelete(deleteConfirm);
                            else await handleDelete(deleteConfirm);
                            setDeleteConfirm(null);
                        }}
                        onCancel={() => setDeleteConfirm(null)} />
                )}

                <Sidebar currentView={currentView} onNavigate={setCurrentView} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <main className="lg:mr-72 min-h-screen">
                    <header className="sticky top-0 z-10 bg-[#0D1F4A]/90 backdrop-blur-xl border-b border-[#1B3F8B]/40 px-6 py-3">
                        <div className="flex items-center justify-between">
                            <button className="lg:hidden p-2 rounded-xl text-blue-400 hover:text-white hover:bg-[#1B3F8B]/30 transition-colors"
                                onClick={() => setSidebarOpen(true)}>
                                <Menu size={22} />
                            </button>
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                                <span className="text-blue-300 text-sm">מגד שמים | מערכת ניהול טיולים</span>
                            </div>
                        </div>
                    </header>
                    <div className="p-6 max-w-6xl mx-auto">{renderContent()}</div>
                </main>
            </div>
        </div>
    );
}

// ─── Export עם AdminGate ──────────────────────────────────
export default function AdminDashboard() {
    return (
        <AdminGate>
            <DashboardApp />
        </AdminGate>
    );
}